/**
 * User Preferences API — aggregated notification view (Phase B).
 *
 * GET /v1/user-preferences/notifications-view[?userId=]
 *
 * Read-only fan-out across the five existing notification stores, tagged with
 * per-source provenance. A store whose read fails is surfaced as an
 * `unavailable` section (its `entries` empty) rather than silently dropped, so
 * an admin never mistakes a failed read for "no preferences". Read-only — no
 * write path in v1, and it does not read `UserPreference`.
 */

import { z } from 'zod'

/** The five notification-preference sources, by stable id. */
export const NOTIFICATION_SOURCES = [
  'group_membership',
  'section_membership',
  'feed_mute',
  'notification_preference',
  'user_notification_settings'
] as const

export const NotificationSourceSchema = z.enum(NOTIFICATION_SOURCES)
export type NotificationSource = z.infer<typeof NotificationSourceSchema>

/** One aggregated notification-preference entry, tagged by the store that produced it. */
export const AggregatedNotificationEntrySchema = z.object({
  source: NotificationSourceSchema,
  /** Stable external id of the underlying row (publicId where one exists). */
  refId: z.string().nullable(),
  /** Human-oriented label for the target this preference applies to. */
  label: z.string().nullable(),
  /** Store-specific notification settings, normalized to a flat record. */
  settings: z.record(z.string(), z.unknown())
})
export type AggregatedNotificationEntry = z.infer<typeof AggregatedNotificationEntrySchema>

/** Per-source provenance section in the aggregate response. */
export const AggregatedNotificationSectionSchema = z.object({
  source: NotificationSourceSchema,
  /** False when this store's read failed; entries are empty and the admin sees `unavailable`. */
  available: z.boolean(),
  entries: z.array(AggregatedNotificationEntrySchema)
})
export type AggregatedNotificationSection = z.infer<typeof AggregatedNotificationSectionSchema>

/** The full read-only aggregate for one subject. */
export const NotificationsViewResponseSchema = z.object({
  sections: z.array(AggregatedNotificationSectionSchema)
})
export type NotificationsViewResponse = z.infer<typeof NotificationsViewResponseSchema>
